import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

export type IslandState = 'idle' | 'building' | 'running';

// Sizes are CSS-transitioned (framer can't interpolate cqw) on the iOS drawer curve — no overshoot.
const Island = styled.div<{ $state: IslandState }>`
  position: absolute;
  top: 2.9cqw;
  left: 50%;
  transform: translateX(-50%);
  z-index: 45;
  background: #000;
  overflow: hidden;
  display: flex;
  align-items: center;
  width: ${props => ({ idle: '32cqw', building: '88cqw', running: '44cqw' }[props.$state])};
  height: ${props => (props.$state === 'building' ? '17cqw' : '9.4cqw')};
  border-radius: ${props => (props.$state === 'building' ? '8.5cqw' : '4.7cqw')};
  transition:
    width 0.5s cubic-bezier(0.32, 0.72, 0, 1),
    height 0.5s cubic-bezier(0.32, 0.72, 0, 1),
    border-radius 0.5s cubic-bezier(0.32, 0.72, 0, 1);
  box-shadow: 0 0 0 0.3cqw rgba(255, 255, 255, 0.04);
`;

const Content = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 3cqw;
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', sans-serif;
`;

const AppIcon = styled.div<{ $size: number }>`
  width: ${props => props.$size}cqw;
  height: ${props => props.$size}cqw;
  border-radius: ${props => props.$size * 0.24}cqw;
  background: #222 center / cover no-repeat;
  flex-shrink: 0;
`;

const BuildText = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 2.6cqw;
  display: flex;
  flex-direction: column;
  gap: 0.4cqw;

  small {
    font-size: 2.6cqw;
    color: rgba(255, 255, 255, 0.55);
  }

  strong {
    font-size: 3.6cqw;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Ring = styled.svg`
  width: 9cqw;
  height: 9cqw;
  flex-shrink: 0;
  transform: rotate(-90deg);
`;

const Live = styled.span`
  width: 1.8cqw;
  height: 1.8cqw;
  border-radius: 50%;
  background: #30d158;
`;

interface DynamicIslandProps {
  state: IslandState;
  icon?: string;
  title?: string;
  subtitle?: string;
  buildMs?: number;
}

const DynamicIsland: React.FC<DynamicIslandProps> = ({ state, icon, title, subtitle, buildMs = 1500 }) => (
  <Island $state={state}>
    <AnimatePresence>
      {state === 'building' && (
        <Content
          key="building"
          initial={{ opacity: 0, filter: 'blur(2px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)', transition: { delay: 0.15, duration: 0.2 } }}
          exit={{ opacity: 0, transition: { duration: 0.12 } }}
        >
          <AppIcon $size={10} style={icon ? { backgroundImage: `url(${icon})` } : undefined} />
          <BuildText>
            <small>{subtitle}</small>
            <strong>{title}</strong>
          </BuildText>
          <Ring viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
            <motion.circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke="#0a84ff"
              strokeWidth="4"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: buildMs / 1000, ease: 'easeInOut' }}
            />
          </Ring>
        </Content>
      )}
      {state === 'running' && (
        <Content
          key="running"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 0.2 } }}
          exit={{ opacity: 0, transition: { duration: 0.1 } }}
        >
          <AppIcon $size={5.6} style={icon ? { backgroundImage: `url(${icon})` } : undefined} />
          <Live />
        </Content>
      )}
    </AnimatePresence>
  </Island>
);

export default DynamicIsland;
